import React, { forwardRef, useImperativeHandle, useMemo } from 'react'
import { Formik, Field, FormikErrors, FieldProps } from 'formik'
import { IntlShape } from 'react-intl'
import { useQuery } from 'react-query'
import Select, { CSSObjectWithLabel, MultiValue } from 'react-select'
import * as Yup from 'yup'
import { getAll } from '../../../../../../services/common_requests'

export interface TourPromotionItem {
  _id?: string
  name: string
  description?: string
  code: string
  promotionType: 'percentage' | 'fixed_amount'
  value: number
  appliesTo: 'all_tours' | 'specific_tours'
  tourIds?: string[]
  startDate?: string
  endDate?: string
  maxUsage?: number
  currentUsage?: number
  minSubtotalTTC?: number
  maxDiscountTTC?: number
  minParticipants?: number
  stackableWithGroupDiscount?: boolean
  currency?: string
  isActive: boolean
}

interface TourOption {
  _id?: string
  id?: string
  title?: string
  name?: string
  translations?: Array<{ title?: string; language?: string }>
}

type TourSelectOption = { value: string; label: string }

const I18N_CONFIG_KEY = process.env.REACT_APP_I18N_CONFIG_KEY || 'i18nConfig'

function getBackofficeLanguage(): string {
  try {
    const raw = localStorage.getItem(I18N_CONFIG_KEY) || '{"selectedLang":"fr"}'
    return JSON.parse(raw).selectedLang || 'fr'
  } catch {
    return 'fr'
  }
}

function extractToursList(response: unknown): TourOption[] {
  if (!response || typeof response !== 'object') return []
  const r = response as Record<string, unknown>
  if (Array.isArray(r.data)) return r.data as TourOption[]
  if (Array.isArray(r)) return r as TourOption[]
  const payload = r.payload as Record<string, unknown> | undefined
  if (payload && Array.isArray(payload.data)) return payload.data as TourOption[]
  return []
}

function tourLabel(tour: TourOption, language: string): string {
  const fromTranslation = tour.translations?.find((t) => t.language === language)?.title
  return tour.title || fromTranslation || tour.name || String(tour._id || tour.id || '')
}

interface PromotionsFormProps {
  initialValues: TourPromotionItem
  onSubmit: (values: TourPromotionItem) => void
  onCancel: () => void
  intl: IntlShape
  tourId: string
  showAllTours?: boolean
  isSubmitting?: boolean
}

const PROMO_CODE_MAX_LENGTH = 10
const PROMO_CODE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'

export function generatePromoCode(length = 8): string {
  const size = Math.min(Math.max(length, 2), PROMO_CODE_MAX_LENGTH)
  let code = ''
  for (let i = 0; i < size; i += 1) {
    code += PROMO_CODE_CHARS.charAt(Math.floor(Math.random() * PROMO_CODE_CHARS.length))
  }
  return code
}

export function sanitizePromoCodeInput(raw: string): string {
  return raw
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, PROMO_CODE_MAX_LENGTH)
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  code: Yup.string()
    .required('Required')
    .min(2)
    .max(PROMO_CODE_MAX_LENGTH)
    .matches(/^[A-Z0-9]+$/, 'Invalid code'),
  promotionType: Yup.string().oneOf(['percentage', 'fixed_amount']).required(),
  value: Yup.number().when('promotionType', {
    is: 'percentage',
    then: (s) => s.min(0.01).max(100),
    otherwise: (s) => s.min(0),
  }),
  appliesTo: Yup.string().oneOf(['all_tours', 'specific_tours']).required(),
  startDate: Yup.date().nullable(),
  endDate: Yup.date().nullable().min(Yup.ref('startDate'), 'End after start'),
  isActive: Yup.boolean().required(),
  stackableWithGroupDiscount: Yup.boolean(),
})

const PromotionsForm = forwardRef<{ handleSubmit: () => void }, PromotionsFormProps>(
  ({ initialValues, onSubmit, onCancel, intl, tourId, showAllTours = false, isSubmitting }, ref) => {
    let formikRef: any = null

    const language = getBackofficeLanguage()

    const {
      data: toursResponse,
      isLoading: toursLoading,
      isFetched: toursFetched,
    } = useQuery(
      ['toursForPromotions', language],
      () => getAll({ size: 500, page: 0, language }, '/tours'),
      { enabled: showAllTours, staleTime: 60_000, retry: 1 },
    )

    const tourSelectOptions: TourSelectOption[] = useMemo(() => {
      return extractToursList(toursResponse)
        .map((tour) => {
          const id = String(tour._id || tour.id || '')
          if (!id) return null
          return { value: id, label: tourLabel(tour, language) || id }
        })
        .filter((o): o is TourSelectOption => Boolean(o))
        .sort((a, b) => a.label.localeCompare(b.label, language))
    }, [toursResponse, language])

    useImperativeHandle(ref, () => ({
      handleSubmit: () => formikRef?.handleSubmit(),
    }))

    return (
      <Formik
        initialValues={{
          ...initialValues,
          promotionType: initialValues.promotionType || 'percentage',
          appliesTo: initialValues.appliesTo || (tourId ? 'specific_tours' : 'all_tours'),
          tourIds: initialValues.tourIds?.length
            ? initialValues.tourIds
            : tourId
              ? [tourId]
              : [],
          stackableWithGroupDiscount: initialValues.stackableWithGroupDiscount !== false,
          startDate: initialValues.startDate
            ? new Date(initialValues.startDate).toISOString().split('T')[0]
            : '',
          endDate: initialValues.endDate
            ? new Date(initialValues.endDate).toISOString().split('T')[0]
            : '',
          isActive: initialValues.isActive ?? true,
          currency: initialValues.currency || 'EUR',
        }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        innerRef={(formik) => {
          formikRef = formik
        }}
      >
        {({ values, errors, touched, setFieldValue }) => {
          const typedErrors = errors as FormikErrors<TourPromotionItem>
          const typedTouched = touched as { [K in keyof TourPromotionItem]?: boolean }
          const showTourPicker =
            showAllTours && values.appliesTo === 'specific_tours'

          const selectedTourOptions = tourSelectOptions.filter((o) =>
            (values.tourIds ?? []).includes(o.value),
          )

          return (
            <div className='form'>
              <div className='row mb-6'>
                <div className='col-md-6'>
                  <label className='form-label required'>
                    {intl.formatMessage({ id: 'TOUR.PROMOTIONS.NAME' })}
                  </label>
                  <Field name='name' className='form-control' />
                  {typedTouched.name && typedErrors.name && (
                    <div className='text-danger fs-7'>{typedErrors.name}</div>
                  )}
                </div>
                <div className='col-md-6'>
                  <label className='form-label required'>
                    {intl.formatMessage({ id: 'TOUR.PROMOTIONS.CODE' })}
                  </label>
                  <div className='input-group'>
                    <Field name='code'>
                      {({ field }: FieldProps<string>) => (
                        <input
                          {...field}
                          type='text'
                          className='form-control text-uppercase'
                          maxLength={PROMO_CODE_MAX_LENGTH}
                          autoComplete='off'
                          value={field.value || ''}
                          onChange={(e) =>
                            setFieldValue('code', sanitizePromoCodeInput(e.target.value))
                          }
                        />
                      )}
                    </Field>
                    <button
                      type='button'
                      className='btn btn-light-primary d-flex align-items-center gap-1 px-4'
                      title={intl.formatMessage({ id: 'TOUR.PROMOTIONS.GENERATE_CODE' })}
                      aria-label={intl.formatMessage({ id: 'TOUR.PROMOTIONS.GENERATE_CODE' })}
                      onClick={() => setFieldValue('code', generatePromoCode(8))}
                    >
                      <i className='bi bi-shuffle fs-4' aria-hidden />
                      <span className='d-none d-md-inline fs-7 fw-semibold'>
                        {intl.formatMessage({ id: 'TOUR.PROMOTIONS.GENERATE_CODE' })}
                      </span>
                    </button>
                  </div>
                  <div className='form-text text-muted'>
                    {intl.formatMessage({ id: 'TOUR.PROMOTIONS.CODE_HINT' })}
                  </div>
                </div>
              </div>

              <div className='row mb-6'>
                <div className='col-md-4'>
                  <label className='form-label required'>
                    {intl.formatMessage({ id: 'TOUR.PROMOTIONS.TYPE' })}
                  </label>
                  <Field as='select' name='promotionType' className='form-select'>
                    <option value='percentage'>
                      {intl.formatMessage({ id: 'TOUR.PROMOTIONS.TYPE_PERCENTAGE' })}
                    </option>
                    <option value='fixed_amount'>
                      {intl.formatMessage({ id: 'TOUR.PROMOTIONS.TYPE_FIXED' })}
                    </option>
                  </Field>
                </div>
                <div className='col-md-4'>
                  <label className='form-label required'>
                    {intl.formatMessage({ id: 'TOUR.PROMOTIONS.VALUE' })}
                  </label>
                  <Field name='value' type='number' className='form-control' step='0.01' />
                </div>
                <div className='col-md-4'>
                  <label className='form-label'>
                    {intl.formatMessage({ id: 'TOUR.PROMOTIONS.CURRENCY' })}
                  </label>
                  <Field name='currency' className='form-control' />
                </div>
              </div>

              <div className='row mb-6'>
                <div className='col-md-6'>
                  <label className='form-label'>
                    {intl.formatMessage({ id: 'TOUR.PROMOTIONS.APPLIES_TO' })}
                  </label>
                  <Field as='select' name='appliesTo' className='form-select'>
                    <option value='all_tours'>
                      {intl.formatMessage({ id: 'TOUR.PROMOTIONS.ALL_TOURS' })}
                    </option>
                    <option value='specific_tours'>
                      {showAllTours
                        ? intl.formatMessage({ id: 'TOUR.PROMOTIONS.SPECIFIC_TOURS' })
                        : intl.formatMessage({ id: 'TOUR.PROMOTIONS.THIS_TOUR' })}
                    </option>
                  </Field>
                </div>
                <div className='col-md-6'>
                  <label className='form-label'>
                    {intl.formatMessage({ id: 'TOUR.PROMOTIONS.MAX_USAGE' })}
                  </label>
                  <Field name='maxUsage' type='number' className='form-control' min={0} />
                </div>
              </div>

              {showTourPicker ? (
                <div className='mb-6'>
                  <label className='form-label required'>
                    {intl.formatMessage({ id: 'TOUR.PROMOTIONS.SELECT_TOURS' })}
                  </label>
                  <p className='text-muted fs-7 mb-3'>
                    {intl.formatMessage({ id: 'TOUR.PROMOTIONS.SELECT_TOURS_HELP' })}
                  </p>
                  <Select
                    isMulti
                    isSearchable
                    isClearable
                    isLoading={toursLoading}
                    options={tourSelectOptions}
                    value={selectedTourOptions}
                    onChange={(selected: MultiValue<TourSelectOption>) =>
                      setFieldValue(
                        'tourIds',
                        selected.length ? selected.map((s) => s.value) : [],
                      )
                    }
                    placeholder={intl.formatMessage({
                      id: 'TOUR.PROMOTIONS.SEARCH_TOURS',
                    })}
                    noOptionsMessage={() =>
                      toursFetched
                        ? intl.formatMessage({ id: 'TOUR.PROMOTIONS.NO_TOURS_FOUND' })
                        : intl.formatMessage({ id: 'COMMON.LOADING' })
                    }
                    classNamePrefix='promo-tour-select'
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                    menuPosition='fixed'
                    styles={{
                      control: (base: CSSObjectWithLabel) => ({ ...base, minHeight: 44, fontSize: 16 }),
                      menuPortal: (base: CSSObjectWithLabel) => ({ ...base, zIndex: 4000 }),
                      menu: (base: CSSObjectWithLabel) => ({ ...base, zIndex: 4000 }),
                    }}
                  />
                  {typedTouched.tourIds && typedErrors.tourIds ? (
                    <div className='text-danger fs-7 mt-2'>
                      {intl.formatMessage({ id: 'TOUR.PROMOTIONS.TOUR_REQUIRED' })}
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className='row mb-6'>
                <div className='col-md-4'>
                  <label className='form-label'>
                    {intl.formatMessage({ id: 'TOUR.PROMOTIONS.MIN_SUBTOTAL' })}
                  </label>
                  <Field name='minSubtotalTTC' type='number' className='form-control' step='0.01' />
                </div>
                <div className='col-md-4'>
                  <label className='form-label'>
                    {intl.formatMessage({ id: 'TOUR.PROMOTIONS.MAX_DISCOUNT' })}
                  </label>
                  <Field name='maxDiscountTTC' type='number' className='form-control' step='0.01' />
                </div>
                <div className='col-md-4'>
                  <label className='form-label'>
                    {intl.formatMessage({ id: 'TOUR.PROMOTIONS.MIN_PARTICIPANTS' })}
                  </label>
                  <Field name='minParticipants' type='number' className='form-control' min={1} />
                </div>
              </div>

              <div className='row mb-6'>
                <div className='col-md-6'>
                  <label className='form-label'>
                    {intl.formatMessage({ id: 'TOUR.PROMOTIONS.START_DATE' })}
                  </label>
                  <Field name='startDate' type='date' className='form-control' />
                </div>
                <div className='col-md-6'>
                  <label className='form-label'>
                    {intl.formatMessage({ id: 'TOUR.PROMOTIONS.END_DATE' })}
                  </label>
                  <Field name='endDate' type='date' className='form-control' />
                </div>
              </div>

              <div className='form-check form-switch mb-6'>
                <Field name='stackableWithGroupDiscount'>
                  {({ field }: FieldProps) => (
                    <input
                      type='checkbox'
                      className='form-check-input'
                      id='promo-stackable-switch'
                      checked={field.value !== false}
                      onChange={(e) =>
                        setFieldValue('stackableWithGroupDiscount', e.target.checked)
                      }
                    />
                  )}
                </Field>
                <label className='form-check-label ms-2' htmlFor='promo-stackable-switch'>
                  {intl.formatMessage({ id: 'TOUR.PROMOTIONS.STACKABLE_LABEL' })}
                </label>
                <p className='text-muted fs-7 mt-2 mb-0 ms-0'>
                  {intl.formatMessage({ id: 'TOUR.PROMOTIONS.STACKABLE_HELP' })}
                </p>
              </div>

              <div className='mb-6'>
                <label className='form-label'>
                  {intl.formatMessage({ id: 'TOUR.PROMOTIONS.DESCRIPTION' })}
                </label>
                <Field as='textarea' name='description' className='form-control' rows={2} />
              </div>

              <div className='form-check form-switch mb-8'>
                <Field name='isActive'>
                  {({ field }: FieldProps) => (
                    <input
                      type='checkbox'
                      className='form-check-input'
                      checked={field.value}
                      onChange={(e) => setFieldValue('isActive', e.target.checked)}
                    />
                  )}
                </Field>
                <label className='form-check-label ms-2'>
                  {intl.formatMessage({ id: 'TOUR.PROMOTIONS.ACTIVE' })}
                </label>
              </div>

              <div className='d-flex justify-content-end gap-3'>
                <button type='button' className='btn btn-light' onClick={onCancel} disabled={isSubmitting}>
                  {intl.formatMessage({ id: 'COMMON.CANCEL' })}
                </button>
                <button
                  type='button'
                  className='btn btn-primary'
                  disabled={isSubmitting}
                  onClick={() => formikRef?.handleSubmit()}
                >
                  {intl.formatMessage({ id: 'COMMON.SAVE' })}
                </button>
              </div>
            </div>
          )
        }}
      </Formik>
    )
  },
)

PromotionsForm.displayName = 'PromotionsForm'
export default PromotionsForm
